import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { BookOpen, Plus, Search, Edit3, Archive, ArchiveRestore, RefreshCw, Loader2 } from "lucide-react";

const API = `${window.location.origin}/api`;

export const formatPence = (pence) =>
  pence === null || pence === undefined ? "—" : `£${(pence / 100).toFixed(2)}`;

export const isValidMoney = (v) => /^\d+(\.\d{1,2})?$/.test(String(v).trim());

const emptyForm = { name: "", supplier: "", supplier_sku: "", unit: "each", default_cost_price: "0.00", default_charge_price: "" };

export const MaterialsCataloguePanel = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // {type: 'archive'|'restore', item}
  const [confirming, setConfirming] = useState(false);

  const headers = { "Content-Type": "application/json", ...(authHeaders?.headers || {}) };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await fetch(`${API}/admin/materials-catalogue?${params}`, { headers });
      if (!res.ok) throw new Error("load failed");
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load catalogue");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => { setEditing(null); setForm({ ...emptyForm }); setIsDialogOpen(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      supplier: item.supplier || "",
      supplier_sku: item.supplier_sku || "",
      unit: item.unit || "each",
      default_cost_price: ((item.default_cost_price_pence || 0) / 100).toFixed(2),
      default_charge_price: item.default_charge_price_pence === null || item.default_charge_price_pence === undefined
        ? "" : (item.default_charge_price_pence / 100).toFixed(2),
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!isValidMoney(form.default_cost_price)) { toast.error("Cost price must be a positive amount with at most 2 decimal places"); return; }
    if (form.default_charge_price.trim() !== "" && !isValidMoney(form.default_charge_price)) {
      toast.error("Charge price must be a positive amount with at most 2 decimal places"); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        supplier: form.supplier.trim(),
        supplier_sku: form.supplier_sku.trim(),
        unit: form.unit.trim() || "each",
        default_cost_price: form.default_cost_price.trim(),
        default_charge_price: form.default_charge_price.trim() === "" ? null : form.default_charge_price.trim(),
      };
      const url = editing ? `${API}/admin/materials-catalogue/${editing.id}` : `${API}/admin/materials-catalogue`;
      const res = await fetch(url, {
        method: editing ? "PATCH" : "POST",
        headers,
        body: JSON.stringify(editing ? { ...payload, version: editing.version } : payload),
      });
      if (res.status === 409) {
        const data = await res.json();
        const existing = data.detail?.existing_item;
        toast.error(existing
          ? `"${existing.name}" already exists in the catalogue`
          : (data.detail?.message || data.detail || "Item was changed by someone else — refreshing"));
        fetchItems();
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(typeof data.detail === "string" ? data.detail : "Failed to save");
        return;
      }
      toast.success(editing ? "Catalogue item updated" : "Catalogue item added");
      setIsDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setConfirming(true);
    try {
      const { type, item } = confirmAction;
      const res = await fetch(`${API}/admin/materials-catalogue/${item.id}/${type}`, {
        method: "POST", headers, body: JSON.stringify({ version: item.version }),
      });
      if (res.status === 409) {
        const data = await res.json();
        const existing = data.detail?.existing_item;
        toast.error(existing
          ? `Cannot restore — "${existing.name}" is already active in the catalogue`
          : "Item was changed by someone else — refreshing");
        fetchItems();
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(typeof data.detail === "string" ? data.detail : "Action failed");
        return;
      }
      toast.success(type === "archive" ? "Item archived" : "Item restored");
      fetchItems();
    } catch (e) {
      toast.error("Action failed");
    } finally {
      setConfirming(false);
      setConfirmAction(null);
    }
  };

  return (
    <Card data-testid="materials-catalogue-panel">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> Materials Catalogue
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={fetchItems} data-testid="catalogue-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button onClick={openAdd} className="bg-accent hover:bg-accent/90" size="sm" data-testid="catalogue-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Reusable materials with default prices. Adding one to a job copies a snapshot — later catalogue edits never change past jobs.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search name, supplier, SKU..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="catalogue-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-full sm:w-40" data-testid="catalogue-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-6 pt-0">
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="catalogue-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="catalogue-empty">
            No catalogue items yet. Add your first reusable material.
          </div>
        ) : (
          <div className="space-y-2" data-testid="catalogue-list">
            {items.map((item) => (
              <div key={item.id}
                className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${item.status === "archived" ? "opacity-60 bg-muted/30" : "bg-white"}`}
                data-testid={`catalogue-item-${item.id}`}>
                <div className="flex-1 min-w-[160px]">
                  <div className="font-medium text-sm flex items-center gap-2">
                    {item.name}
                    {item.status === "archived" && <Badge variant="outline" className="text-[10px]">Archived</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[item.supplier, item.supplier_sku, item.unit].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div data-testid={`catalogue-item-cost-${item.id}`}>Cost: <span className="font-semibold">{formatPence(item.default_cost_price_pence)}</span></div>
                  <div className="text-muted-foreground" data-testid={`catalogue-item-charge-${item.id}`}>Charge: {formatPence(item.default_charge_price_pence)}</div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => openEdit(item)}
                    aria-label={`Edit ${item.name}`} data-testid={`catalogue-edit-${item.id}`}>
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  {item.status === "active" ? (
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0"
                      onClick={() => setConfirmAction({ type: "archive", item })}
                      aria-label={`Archive ${item.name}`} data-testid={`catalogue-archive-${item.id}`}>
                      <Archive className="h-4 w-4 text-amber-600" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0"
                      onClick={() => setConfirmAction({ type: "restore", item })}
                      aria-label={`Restore ${item.name}`} data-testid={`catalogue-restore-${item.id}`}>
                      <ArchiveRestore className="h-4 w-4 text-green-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add / Edit dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="catalogue-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit catalogue item" : "Add catalogue item"}</DialogTitle>
            <DialogDescription>
              Default prices are copied (snapshotted) onto jobs — editing here never changes past jobs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="catalogue-form-name">Name *</Label>
              <Input id="catalogue-form-name" value={form.name}
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. 18mm Plywood Sheet" data-testid="catalogue-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="catalogue-form-supplier">Supplier</Label>
                <Input id="catalogue-form-supplier" value={form.supplier}
                  onChange={(e) => setForm(p => ({ ...p, supplier: e.target.value }))}
                  data-testid="catalogue-form-supplier" />
              </div>
              <div>
                <Label htmlFor="catalogue-form-sku">Supplier SKU</Label>
                <Input id="catalogue-form-sku" value={form.supplier_sku}
                  onChange={(e) => setForm(p => ({ ...p, supplier_sku: e.target.value }))}
                  data-testid="catalogue-form-sku" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="catalogue-form-unit">Unit</Label>
                <Input id="catalogue-form-unit" value={form.unit}
                  onChange={(e) => setForm(p => ({ ...p, unit: e.target.value }))}
                  data-testid="catalogue-form-unit" />
              </div>
              <div>
                <Label htmlFor="catalogue-form-cost">Cost £ *</Label>
                <Input id="catalogue-form-cost" inputMode="decimal" value={form.default_cost_price}
                  onChange={(e) => setForm(p => ({ ...p, default_cost_price: e.target.value }))}
                  placeholder="0.00" data-testid="catalogue-form-cost" />
              </div>
              <div>
                <Label htmlFor="catalogue-form-charge">Charge £</Label>
                <Input id="catalogue-form-charge" inputMode="decimal" value={form.default_charge_price}
                  onChange={(e) => setForm(p => ({ ...p, default_charge_price: e.target.value }))}
                  placeholder="Optional" data-testid="catalogue-form-charge" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Charge price is for reference only — it never feeds invoices or quotes.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="catalogue-form-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="catalogue-form-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : editing ? "Save changes" : "Add item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive / Restore confirmation */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="max-w-sm" data-testid="catalogue-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "archive" ? "Archive this item?" : "Restore this item?"}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === "archive"
                ? `"${confirmAction?.item?.name}" will be hidden from pickers but kept forever — nothing is deleted and past jobs are unaffected.`
                : `"${confirmAction?.item?.name}" will become available in pickers again.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} data-testid="catalogue-confirm-cancel">Cancel</Button>
            <Button onClick={handleConfirm} disabled={confirming}
              variant={confirmAction?.type === "archive" ? "destructive" : "default"}
              data-testid="catalogue-confirm-btn">
              {confirming ? "Working…" : confirmAction?.type === "archive" ? "Archive" : "Restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MaterialsCataloguePanel;
